/**
 * Email-message HTML sanitizer + iframe-document builder.
 *
 * Email bodies are UNTRUSTED third-party HTML from arbitrary senders — a wholly
 * different threat model from chat HTML (which our own TipTap composer produces
 * against a tiny allow-list and which `sanitize-chat-html.ts` injects straight
 * into the app DOM). A marketing or phishing email can carry:
 *   - classic `<script>` / `onerror=` / `javascript:` XSS,
 *   - tracking pixels and remote images that deanonymise the reader on open,
 *   - `<style>`/inline CSS that would break out and restyle the whole host app,
 *   - credential-harvesting `<form>`s and `<base>`/`<meta refresh>` redirects.
 *
 * Defence is two-layered and BOTH layers are required:
 *   1. This module sanitizes with a broad-but-safe email allow-list (DOMPurify),
 *      hardens links, and (by default) neutralises remote images.
 *   2. The caller (`EmailHtmlBody`) renders the returned document inside a
 *      sandboxed iframe with NO `allow-scripts`, so the email's CSS is isolated
 *      and any residual markup can never touch the host document.
 *
 * The returned `document` string is a complete `<!doctype html>` page: the
 * sanitized body wrapped with a locked-down CSP `<meta>` and a small base
 * stylesheet, ready to drop into the iframe's `srcDoc`.
 *
 * We deliberately do NOT register DOMPurify hooks here. `sanitize-chat-html.ts`
 * adds a global `afterSanitizeAttributes` hook to the shared singleton; adding
 * our own would run on chat sanitization too (and vice-versa). All email-only
 * behaviour (link hardening, remote-image blocking) happens in an explicit
 * post-sanitize DOM walk instead, so the two consumers never perturb each other.
 */

import DOMPurify from 'dompurify';

// Broad enough to preserve real email layout (tables, inline styles, fonts,
// images) but with every script/redirect/credential-capture vector forbidden.
// CSS isolation is provided by the iframe, so allowing `<style>` and inline
// `style` is safe — it cannot escape the frame.
const FORBID_TAGS = [
  'script',
  'iframe',
  'frame',
  'frameset',
  'object',
  'embed',
  'applet',
  'base', // could rewrite every relative URL / default link target
  'meta', // strip the sender's own meta (refresh redirects); we inject our own
  'link', // external stylesheets / prefetch
  'form',
  'input',
  'button',
  'textarea',
  'select',
  'option',
  'title',
  'noscript',
];

// `srcset`/`sizes` would fetch remote images outside our `src` control; `ping`
// and `formaction` are exfiltration vectors. Event handlers (`on*`) are dropped
// by DOMPurify itself.
const FORBID_ATTR = ['srcset', 'sizes', 'ping', 'formaction'];

// Presentational attributes that legitimate (often table-based) email layouts
// rely on. DOMPurify's html profile covers most; we add the deprecated ones it
// omits so old-school email tables keep their structure.
const ADD_ATTR = [
  'target',
  'style',
  'align',
  'valign',
  'bgcolor',
  'background',
  'color',
  'width',
  'height',
  'cellpadding',
  'cellspacing',
  'border',
  'dir',
  'face',
  'size',
];

// Keep `<style>`/document-shell tags for whole-document sanitization so that CSS
// declared in `<head>` (very common in marketing email) survives.
const ADD_TAGS = ['style', 'html', 'head', 'body'];

// http(s):// or protocol-relative //host — i.e. something the browser would
// fetch off-origin. `cid:`, `data:`, `blob:` and relative URLs are not remote.
const REMOTE_URL = /^\s*(?:https?:)?\/\//i;
const REMOTE_CSS_URL = /url\(\s*['"]?\s*(?:https?:)?\/\//i;

function isRemoteUrl(value: string | null | undefined): boolean {
  return !!value && REMOTE_URL.test(value);
}

/**
 * Canonical key for matching a `cid:` reference to a stored attachment's
 * Content-ID. Senders quote Content-IDs inconsistently (angle brackets, case),
 * so both sides normalize the same way before comparison.
 */
export function normalizeContentId(cid: string): string {
  return cid.trim().replace(/^</, '').replace(/>$/, '').toLowerCase();
}

export interface SanitizedEmail {
  /** Complete `<!doctype html>` document for the iframe `srcDoc`. */
  document: string;
  /** True when the message references remote images/backgrounds we suppressed. */
  hasRemoteImages: boolean;
}

export interface SanitizeEmailOptions {
  /**
   * When true (the default), remote images and CSS background images are
   * suppressed so opening a message can't phone home to the sender. The caller
   * exposes a "Display images" control that re-sanitizes with this set false.
   */
  blockRemoteImages?: boolean;
  /**
   * Map of normalized Content-ID (see `normalizeContentId`) → resolved URL for
   * the message's own inline images (referenced via `cid:`). The caller builds
   * this from the message attachments. Inline images are message content, not
   * tracker pixels, so they render regardless of `blockRemoteImages`; their URL
   * origin is added to the CSP so the (otherwise locked-down) iframe may load
   * them. A `cid:` with no entry has its `src` dropped (no broken icon).
   */
  cidUrlMap?: Record<string, string>;
}

/**
 * Sanitize an untrusted email HTML body and wrap it into a self-contained,
 * locked-down document for sandboxed-iframe rendering.
 */
export function sanitizeEmailHtml(
  dirty: string,
  options: SanitizeEmailOptions = {},
): SanitizedEmail {
  const blockRemoteImages = options.blockRemoteImages ?? true;
  const cidUrlMap = options.cidUrlMap ?? {};

  // Whole-document mode preserves `<head><style>`; DOMPurify still strips every
  // forbidden tag/attribute. The CSS inside surviving `<style>`/style attrs is
  // run through DOMPurify's own CSS sanitizer (drops expression()/@import/etc.).
  // RETURN_DOM hands back nodes in DOMPurify's INERT document, so the link
  // hardening and remote-image walk below never trigger a resource fetch (a live
  // DOMParser document would eagerly load every img/iframe src).
  const root = DOMPurify.sanitize(dirty, {
    WHOLE_DOCUMENT: true,
    USE_PROFILES: { html: true },
    ADD_TAGS,
    ADD_ATTR,
    FORBID_TAGS,
    FORBID_ATTR,
    ALLOW_UNKNOWN_PROTOCOLS: false,
    RETURN_DOM: true,
  }) as unknown as HTMLElement;

  // `root` is a live element in DOMPurify's inert document; ownerDocument is
  // always present here.
  const doc = root.ownerDocument as Document;

  // Belt-and-suspenders: explicitly drop any forbidden element DOMPurify's
  // tree-walk may have left orphaned (a known jsdom/happy-dom parser quirk; a
  // no-op in a real browser where FORBID_TAGS already removed them).
  for (const el of Array.from(doc.querySelectorAll(FORBID_TAGS.join(',')))) {
    el.remove();
  }

  let hasRemoteImages = false;
  // Origins of resolved inline (`cid:`) images we must whitelist in the CSP.
  const cidOrigins = new Set<string>();

  // Links always open in a new top-level tab, fully detached from this origin.
  for (const a of Array.from(doc.querySelectorAll('a[href]'))) {
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noopener noreferrer nofollow');
  }

  for (const img of Array.from(doc.querySelectorAll('img'))) {
    img.removeAttribute('srcset');
    const src = img.getAttribute('src') ?? '';

    // Inline image: rewrite `cid:<content-id>` to the resolved attachment URL.
    if (/^\s*cid:/i.test(src)) {
      const resolved = cidUrlMap[normalizeContentId(src.replace(/^\s*cid:/i, ''))];
      if (resolved) {
        img.setAttribute('src', resolved);
        try {
          cidOrigins.add(new URL(resolved).origin);
        } catch {
          /* data:/blob: URLs have no origin and need no CSP entry */
        }
      } else {
        // Unresolved inline image (not yet loaded, or missing attachment).
        img.removeAttribute('src');
      }
      continue;
    }

    if (isRemoteUrl(src)) {
      hasRemoteImages = true;
      // Drop the src in blocked mode so the reader sees nothing (not a broken
      // icon) and, more importantly, the browser never issues the request.
      if (blockRemoteImages) img.removeAttribute('src');
    }
  }

  // `background="http…"` table attrs and `url(http…)` inside inline/`<style>`
  // CSS are remote fetches too. The iframe CSP blocks the actual load in blocked
  // mode; here we only need to detect them so the banner appears.
  for (const el of Array.from(doc.querySelectorAll('[background]'))) {
    if (isRemoteUrl(el.getAttribute('background'))) hasRemoteImages = true;
  }
  for (const el of Array.from(doc.querySelectorAll('[style]'))) {
    if (REMOTE_CSS_URL.test(el.getAttribute('style') ?? '')) hasRemoteImages = true;
  }
  for (const styleEl of Array.from(doc.querySelectorAll('style'))) {
    if (REMOTE_CSS_URL.test(styleEl.textContent ?? '')) hasRemoteImages = true;
  }

  injectHead(doc, blockRemoteImages, cidOrigins);

  return {
    document: `<!doctype html>${doc.documentElement.outerHTML}`,
    hasRemoteImages,
  };
}

/**
 * Prepend the security CSP and base stylesheet to the document head. The CSP is
 * the enforcement layer (the DOM walk above is only for the banner heuristic):
 * `default-src 'none'` blocks scripts/frames/connections outright, and `img-src`
 * is the toggle that actually permits or denies remote images.
 */
function injectHead(doc: Document, blockRemoteImages: boolean, cidOrigins: Set<string>): void {
  const head = doc.head ?? doc.documentElement.appendChild(doc.createElement('head'));

  // Inline (`cid:`) image origins are always permitted — they're message
  // content. In blocked mode that's the ONLY remote origin allowed; the open
  // mode already permits any image source.
  const inlineImgSrc = ['data:', ...cidOrigins].join(' ');
  const imgSrc = blockRemoteImages ? `img-src ${inlineImgSrc}` : 'img-src * data: blob:';
  const csp = doc.createElement('meta');
  csp.setAttribute('http-equiv', 'Content-Security-Policy');
  csp.setAttribute(
    'content',
    [
      "default-src 'none'",
      imgSrc,
      "style-src 'unsafe-inline'",
      blockRemoteImages ? 'font-src data:' : 'font-src * data:',
      'media-src data:',
    ].join('; '),
  );

  const base = doc.createElement('style');
  base.textContent = BASE_CSS;

  // Insert first so the email's own `<style>` rules win on equal specificity.
  head.insertBefore(base, head.firstChild);
  head.insertBefore(csp, head.firstChild);
}

// Fallback presentation only — the email's own inline styles override these.
// Forces images to fit, wraps long unbreakable strings, and renders on white
// (email content universally assumes a light background, like every webmail).
const BASE_CSS = `
html { -webkit-text-size-adjust: 100%; overflow-x: hidden; }
body {
  margin: 0;
  padding: 12px 14px;
  background: #ffffff;
  color: #1f2937;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  font-size: 14px;
  line-height: 1.5;
  word-wrap: break-word;
  overflow-wrap: break-word;
}
img { max-width: 100%; height: auto; }
table { max-width: 100%; }
a { color: #2563eb; }
`.trim();
